import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { CustomBreadCrumb } from "@/components/customBreadCrumb";
import "./style.scss";
import { CustomInput, CustomPagination, CustomSwitch, CustomTable, PaginationText } from "@/components";
import { FileSignature, Info, Shield } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AdminType, RoleType } from "@/DTO";
import { useAppPagination } from "@/Router/hook";
import { AdminApi, RoleApi } from "@/api";
import { useAppSelector } from "@/redux-config/hooks";
import { selectUser } from "@/redux-config/entities/user";
import { InsideLoading } from "@/components/loading/inside";
import { selectLanguage } from "@/redux-config/entities/language";
import { ConvertDate, ConvertNumber } from "@/utils";
import { SearchComponent } from "@/components/search";
import { CustomModal } from "@/components/custom-modal";

export const AdminList: React.FC<{}> = () => {
  const { t } = useTranslation();

  const crumbs = [
    { name: t("Dashboard"), href: "/" },
    { name: t("Admin"), href: "#" },
    { name: t("List"), href: "#" }
  ];

  const navigate = useNavigate();
  const lang = useAppSelector(selectLanguage);
  const user = useAppSelector(selectUser);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState(useAppPagination());

  const [data, setData] = useState<AdminType[]>([]);
  const [searchData, setSearchData] = useState<string>();

  const [userId, setUserId] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [roleText, setRoleText] = useState<string>("");
  const [roleFromServer, setRoleFromServer] = useState<RoleType[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleType[]>([]);
  const [showRoleList, setShowRoleList] = useState<boolean>(false);

  const fetchRoleList = () => {
    RoleApi.GetRoleList(user, { currentPage: 1, perPage: 10,total:10 }, roleText).then(
      (data) => {
        setRoleFromServer(data.data);
      }
    );
  };

  const assignRoleToAdmin = () => {
    const roleIdList = selectedRole.map((item) => item.id);
    setLoading(true);
    AdminApi.RoleAssignAdmin(user, userId, roleIdList)
      .then((data) => {
        setUserId('');
        setShowModal(false);
        setSelectedRole([]);
        setLoading(false);
        handleGetData(pagination.currentPage);
      })
      .catch((err) => {
        setLoading(false);
        setShowModal(false);
      });
  };

  const handleGetData = (page: number) => {
    setLoading(true);
    AdminApi.GetAdminList(user, { ...pagination, currentPage: page }, searchData).then((data) => {
      setData(data.data);
      setPagination(data.pagination);
      setLoading(false);
    }).catch((err) => {

      setLoading(false);
    });
  };

  useEffect(() => {
    handleGetData(pagination.currentPage);
  }, []);

  const handleChangeStatus = (id: string) => {
    AdminApi.ChangeStatusAdmin(user, id).then((item) => {
    }).catch((err) => {
    });
  };

  return (
    <>
      <Helmet>
        <title>MahdiBookShop | Admin List</title>
        <meta name="description" content="Basic example" />
      </Helmet>
      <div className="d-flex flex-column">
        <CustomBreadCrumb breadCrumbList={crumbs} />
        <div className="w-100 p-2">
          <div className="rounded-3 py-2 bg-custom-white h-100 d-flex flex-column justify-content-between">
            <div className={"d-flex flex-row gap-2 align-items-center"}>
              <h5 className={"p-3"}>{t("AdminList")}</h5>
              <CustomInput type={"text_number"} name={"search-for-list"} placeholder={t("search") + ""}
                           className={"custom-input border-reg rounded-5"} onChange={setSearchData}
                           value={searchData} />
              <SearchComponent fetchDataApi={() => {
                if(searchData&&searchData?.length>0){
                  handleGetData(1);
                }else {
                  handleGetData(pagination.currentPage);
                }
              }} name={searchData} />
            </div>
            {loading ? <InsideLoading /> :
              <>
                <CustomTable
                  header={[
                    <span>{t("No")}</span>,
                    <span>{t("Name")}</span>,
                    <span>{t("Phone")}</span>,
                    <span>{t("RegisterDate")}</span>,
                    <span>{t("Role")}</span>,
                    <span>{t("status")}</span>,
                    <span>{t("Options")}</span>
                  ]}
                  items={[
                    ...data.map((item, key) => [
                      <span>{ConvertNumber(key + 1 + (pagination.currentPage - 1) * pagination.perPage + "", lang)}</span>,
                      <span>{item.first_name + " " + item.last_name}</span>,
                      <a href={`tel:${item.phone}`}>{ConvertNumber(item.phone + "", lang)}</a>,
                      <span>{ConvertDate(item.created_at + "", lang)}</span>,
                      <span
                        className={"d-flex flex-row gap-2 flex-wrap justify-content-center"}>{item.role?.map((item, index) => (
                        <span key={index}>{item.title}</span>))}</span>,
                      <CustomSwitch defaultChecked={item.status === "active"} onClick={() => {
                        handleChangeStatus(item.id);
                      }} />,
                      <div
                        className={"d-flex flex-row gap-1 justify-content-center"}
                      >
                        <button
                          className={
                            "btn p-1 align-baseline rounded-circle bg-custom-light"
                          }
                          onClick={() => {
                            setShowModal(true);
                            setUserId(item.id + '');
                            setUserName(item.first_name + ' ' + item.last_name);
                            setSelectedRole(item.role ? item.role : []);
                          }}
                        >
                          <Shield />
                        </button>
                        <Link
                          to={`/admin/edit/${item.id}`}
                          className={
                            "btn p-1 align-baseline rounded-circle bg-custom-light"
                          }
                        >
                          <FileSignature />
                        </Link>
                        <Link
                          to={`/admin/info/${item.id}`}
                          className={
                            "btn p-1 align-baseline rounded-circle bg-custom-light"
                          }
                        >
                          <Info />
                        </Link>
                      </div>
                    ])
                  ]}
                />
                <div
                  className={
                    "d-flex flex-row p-3 align-items-center justify-content-between"
                  }
                >
              <span>
                <PaginationText pagination={pagination} />
              </span>
                  <CustomPagination
                    currentPage={pagination.currentPage}
                    perPage={pagination.perPage}
                    changePage={(data: string) => {
                      navigate(`/admin?current_page=${data}`);
                      setPagination({ ...pagination, currentPage: +data });
                      handleGetData(+data);
                    }}
                    total={pagination.total}
                  />
                </div>
              </>
            }
          </div>
        </div>
      </div>
      <CustomModal showModal={showModal} setShowModal={setShowModal}>
        <div className={'d-flex flex-column gap-3'}>
            <span>
              {t('Name')}: {userName}
            </span>
          <div
            className={'d-flex flex-column position-relative'}
            onMouseLeave={() => {
              setShowRoleList(false);
            }}
          >
            <span>{t('RoleName')}</span>
            <input
              className={`custom-input border-reg}`}
              value={roleText}
              onChange={(e) => {
                setRoleText(e.target.value);
              }}
              onClick={() => {
                setShowRoleList(true);
              }}
            />
            <div className={'d-flex flex-row flex-wrap p-2 gap-2'}>
              {selectedRole.map((item, index) => (
                <div
                  className={
                    'd-flex flex-row gap-2 text-white p-2 rounded-2 blue3-bg'
                  }
                >
                  <span key={index}>{item.title}</span>
                  <span
                    className={'g-cursor-pointer'}
                    onClick={() => {
                      const list = selectedRole.filter(
                        (role) => role.id != item.id,
                      );
                      setSelectedRole([...list]);
                    }}
                  >
                      x
                    </span>
                </div>
              ))}
            </div>

            {showRoleList ? (
              <div
                className={
                  'position-absolute d-flex flex-column shadow-sm bg-custom-white text-custom-dark rounded-3 w-100'
                }
                style={{ zIndex: 10, top: '3.9rem' }}
              >
                {roleFromServer.map((item, index) => (
                  <span
                    className={`p-2 text-center g-cursor-pointer ${
                      index != roleFromServer.length
                        ? 'border-bottom'
                        : 'border-0'
                    }`}
                    key={index}
                    onClick={() => {
                      setShowRoleList(false);
                      const list = selectedRole.filter(
                        (role) => role.id != item.id,
                      );
                      list.push(item);
                      setSelectedRole([...list]);
                    }}
                  >
                      {item.title}
                    </span>
                ))}
              </div>
            ) : (
              <></>
            )}
          </div>
          <div>
            <button
              className={'btn prm-btn rounded-5 px-4 mt-3'}
              disabled={selectedRole.length == 0}
              onClick={() => {
                assignRoleToAdmin();
              }}
            >
              {t('submit')}
            </button>
          </div>
        </div>
      </CustomModal>
      <SearchComponent name={roleText} fetchDataApi={fetchRoleList} />
    </>
  );
};
